import { ref, computed } from "vue";
import type { ISatMapProps } from "@chatfood/design-system";
import type { IDomainOutletPickup } from "~/domain/outlet-pickup";
import type { IDomainCoordinate } from "~/domain/coordinate";
import { useBusinessStore } from "~/stores/business";
import { useNavigation } from "~/composables/use-navigation";

export const usePickup = () => {
  const business = useBusinessStore();
  const { push } = useNavigation();

  const isLoading = ref(false);
  const locationsSource = ref<IDomainOutletPickup[]>([]);
  const clickedLocation = ref<IDomainOutletPickup>();
  const focusedLocation = ref<IDomainOutletPickup>();
  const currentGeoLocation = ref<IDomainCoordinate>();

  const mapBoundaries = ref<ISatMapProps["mapBounds"]>({
    location: business.country,
  });

  const locations = computed(() => {
    return locationsSource.value.map((location) => ({
      ...location,
      isSelected: clickedLocation.value?.outletId === location.outletId,
      isFocused: focusedLocation.value?.outletId === location.outletId,
    }));
  });

  const updateMapBoundaries = () => {
    const allCoordinates = [
      ...(currentGeoLocation.value ? [currentGeoLocation.value] : []),
      ...(locations.value.length
        ? locations.value.map((location) => location.coordinates)
        : []),
    ];

    const lats = allCoordinates.map((coord) => coord.lat);
    const lngs = allCoordinates.map((coord) => coord.lng);

    const bounds = {
      north: Math.max(...lats),
      south: Math.min(...lats),
      east: Math.max(...lngs),
      west: Math.min(...lngs),
    };

    mapBoundaries.value = {
      location: business.country,
      bounds,
    };
  };

  const onLocationsLoaded = (data: IDomainOutletPickup[]) => {
    locationsSource.value = data;
    updateMapBoundaries();
  };

  const onLocationClicked = (location: IDomainOutletPickup) => {
    clickedLocation.value = location;

    mapBoundaries.value = {
      location: business.country,
      coordinates: {
        lat: clickedLocation.value.coordinates.lat,
        lng: clickedLocation.value.coordinates.lng,
      },
      bounds: undefined,
    };
  };

  const onLocationFocused = (location: IDomainOutletPickup) => {
    focusedLocation.value = location;
  };

  const onLocationBlur = () => {
    focusedLocation.value = undefined;
  };

  const onPickupAt = async (outletSlug: string) => {
    isLoading.value = true;

    try {
      await push("outlet", {
        orderingMode: "pickup",
        outletSlug,
      });
    } catch {
      isLoading.value = false;
    }
  };

  const setCurrentGeoLocation = (coordinate?: IDomainCoordinate) => {
    currentGeoLocation.value = coordinate;
    updateMapBoundaries();
  };

  return {
    mapBoundaries,
    isLoading,
    clickedLocation,
    focusedLocation,
    locations,
    currentGeoLocation,
    onLocationsLoaded,
    onLocationClicked,
    onLocationBlur,
    onLocationFocused,
    updateMapBoundaries,
    onPickupAt,
    setCurrentGeoLocation,
  };
};
